import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

const socket = io("https://apexalgo.duckdns.org", {
  path: "/live-socket/socket.io/",
  autoConnect: false,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
});

const styles = {
  page: {
    padding: 12,
    width: "100%",
    boxSizing: "border-box",
    color: "#e5e7eb",
    background: "#111827",
    minHeight: "100vh",
    fontFamily: "Arial, sans-serif",
  },

  card: {
    background: "#1f2937",
    border: "1px solid #374151",
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },
  label: {
    color: "#9ca3af",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.5px",
  },
  input: {
    padding: "8px 10px",
    background: "#111827",
    color: "#f9fafb",
    border: "1px solid #4b5563",
    borderRadius: 7,
    minWidth: 0,
    width: "100%",
    height: 36,
    boxSizing: "border-box",
    fontSize: 13,
    outline: "none",
  },

  button: {
    padding: "8px 14px",
    border: 0,
    borderRadius: 6,
    fontWeight: 700,
    cursor: "pointer",
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    padding: 10,
    textAlign: "left",
    color: "#9ca3af",
    borderBottom: "1px solid #374151",
    whiteSpace: "nowrap",
  },
  td: {
    padding: 10,
    borderBottom: "1px solid #374151",
    whiteSpace: "nowrap",
  },
};

function money(value) {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value))
  ) {
    return "—";
  }

  return Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
  });
}

function positionBelongsToIndex(position, index) {
  const symbol = String(position?.symbol || "")
    .trim()
    .toUpperCase();

  if (index === "NIFTY") {
    return /^(?:NSE:)?NIFTY(?=\d|[-_ ])/.test(symbol);
  }

  if (index === "SENSEX") {
    return /^(?:BSE:)?SENSEX(?=\d|[-_ ])/.test(symbol);
  }

  return false;
}

function parseOptionSymbol(symbolValue) {
  const symbol = String(symbolValue || "")
    .trim()
    .toUpperCase();

  // Supports both:
  // NSE:NIFTY26N0323550PE  (compact expiry format)
  // NSE:NIFTY26OCT23550PE  (three-letter month format)
  const match = symbol.match(
    /^(?:NSE:|BSE:)?(NIFTY|SENSEX)(\d{2}(?:[A-Z]{3}|[A-Z]\d{2}))(\d+)(CE|PE)$/,
  );

  if (!match) return null;

  return {
    index: match[1],
    expiry: match[2],
    strike: match[3],
    optionType: match[4],
  };
}

function parsePayoffOption(position) {
  const symbol = String(position?.symbol || "")
    .trim()
    .toUpperCase();

  const match = symbol.match(
    /^(?:NSE:|BSE:)?(NIFTY|SENSEX)(\d{2}(?:[A-Z]{3}|[A-Z]\d{2}))(\d+)(CE|PE)$/,
  );

  if (!match) return null;

  const [, index, expiry, strikeText, optionType] = match;
  const netQty = Number(position.netQty);

  if (!Number.isFinite(netQty) || netQty === 0) {
    return null;
  }

  // Prefer the net average, consistent with the existing positions table.
  // Fall back to the side-specific average if netAvg is missing or zero.
  const entryCandidates = [
    position.netAvg,
    netQty > 0 ? position.buyAvg : position.sellAvg,
    position.buyAvg,
    position.sellAvg,
  ];

  let entryPrice = null;

  for (const candidate of entryCandidates) {
    if (candidate === null || candidate === undefined || candidate === "") {
      continue;
    }

    const numericCandidate = Number(candidate);

    if (Number.isFinite(numericCandidate) && numericCandidate > 0) {
      entryPrice = numericCandidate;
      break;
    }
  }

  if (entryPrice === null) {
    return {
      index,
      expiry,
      strike: Number(strikeText),
      optionType,
      netQty,
      entryPrice: null,
      symbol,
    };
  }

  return {
    index,
    expiry,
    strike: Number(strikeText),
    optionType,
    netQty,
    entryPrice,
    symbol,
  };
}

function calculateOptionExpiryPnl(position, underlyingPrice) {
  const intrinsicValue =
    position.optionType === "CE"
      ? Math.max(underlyingPrice - position.strike, 0)
      : Math.max(position.strike - underlyingPrice, 0);

  // netQty is signed:
  // positive = BUY, negative = SELL.
  // Quantities are broker units, not lot counts.
  return position.netQty * (intrinsicValue - position.entryPrice);
}

function buildExpiryPayoffModel(brokerPositions) {
  const openPositions = (brokerPositions || []).filter((position) => {
    const qty = Number(position?.netQty);
    return Number.isFinite(qty) && qty !== 0;
  });

  if (openPositions.length === 0) {
    return { error: "No open positions are available to chart." };
  }

  const parsedPositions = openPositions.map(parsePayoffOption);

  if (parsedPositions.some((position) => position === null)) {
    return {
      error:
        "At least one open position is not a recognized NIFTY/SENSEX option. " +
        "The combined payoff is withheld rather than silently omitting a position.",
    };
  }

  if (parsedPositions.some((position) => position.entryPrice === null)) {
    return {
      error:
        "An option entry price is missing or invalid. Refresh FYERS positions before calculating payoff.",
    };
  }

  const indexes = new Set(parsedPositions.map((position) => position.index));
  const expiries = new Set(parsedPositions.map((position) => position.expiry));

  if (indexes.size !== 1 || expiries.size !== 1) {
    return {
      error:
        "Open options span different underlyings or expiries. " +
        "A single expiry-payoff curve cannot safely combine them.",
    };
  }

  const strikes = [
    ...new Set(parsedPositions.map((position) => position.strike)),
  ].sort((a, b) => a - b);

  const payoffAt = (underlyingPrice) =>
    parsedPositions.reduce(
      (total, position) =>
        total + calculateOptionExpiryPnl(position, underlyingPrice),
      0,
    );

  // For a piecewise-linear options payoff, finite extrema occur at
  // spot = 0 or at one of the option strikes.
  const candidatePrices = [...new Set([0, ...strikes])];
  const candidatePayoffs = candidatePrices.map((price) => ({
    price,
    pnl: payoffAt(price),
  }));

  const upperCallSlope = parsedPositions
    .filter((position) => position.optionType === "CE")
    .reduce((total, position) => total + position.netQty, 0);

  const finiteBest = Math.max(...candidatePayoffs.map((point) => point.pnl));

  const finiteWorst = Math.min(...candidatePayoffs.map((point) => point.pnl));

  // Above every strike, the slope equals total signed call quantity.
  // Positive slope => unbounded profit; negative slope => unbounded loss.
  const maxProfitUnlimited = upperCallSlope > 0;
  const maxLossUnlimited = upperCallSlope < 0;

  return {
    positions: parsedPositions,
    index: parsedPositions[0].index,
    expiry: parsedPositions[0].expiry,
    strikes,
    payoffAt,
    candidatePayoffs,
    maxProfitUnlimited,
    maxLossUnlimited,
    bestExpiryPnl: maxProfitUnlimited ? null : finiteBest,
    worstExpiryPnl: maxLossUnlimited ? null : finiteWorst,
  };
}

function findPayoffBreakevens(payoffAt, breakpoints) {
  const sorted = [...new Set(breakpoints)].sort((a, b) => a - b);
  const roots = [];

  for (const point of sorted) {
    if (Math.abs(payoffAt(point)) < 0.000001) {
      roots.push(point);
    }
  }

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const x1 = sorted[i];
    const x2 = sorted[i + 1];
    const y1 = payoffAt(x1);
    const y2 = payoffAt(x2);

    if (y1 * y2 < 0) {
      // Linear interpolation is exact within a strike interval.
      const root = x1 + ((0 - y1) * (x2 - x1)) / (y2 - y1);
      roots.push(root);
    }
  }

  return roots
    .filter((root) => Number.isFinite(root) && root >= 0)
    .sort((a, b) => a - b)
    .filter(
      (root, index, all) =>
        index === 0 || Math.abs(root - all[index - 1]) > 0.01,
    );
}

function PayoffChart({ positions, spot, stale, loading, index }) {
  const model = buildExpiryPayoffModel(positions);
  const currentSpot =
    spot !== null &&
    spot !== undefined &&
    spot !== "" &&
    Number.isFinite(Number(spot)) &&
    Number(spot) > 0
      ? Number(spot)
      : null;

  const chartWidth = 760;
  const chartHeight = 320;
  const margin = { top: 24, right: 24, bottom: 48, left: 76 };

  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;

  const panelStyle = {
    background: "#111827",
    border: "1px solid #374151",
    borderRadius: 8,
    padding: 13,
    minWidth: 0,
  };

  const labelStyle = {
    color: "#9ca3af",
    fontSize: 12,
    marginBottom: 7,
  };

  const numberStyle = {
    color: "#f9fafb",
    fontSize: 18,
    fontWeight: 700,
    overflowWrap: "anywhere",
  };

  const pnlColor = (value) =>
    value > 0 ? "#34d399" : value < 0 ? "#f87171" : "#f9fafb";

  const formatPnl = (value) => `${value > 0 ? "+" : ""}₹${money(value)}`;

  if (model.error) {
    return (
      <section style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Position Payoff — at Expiry</h3>

        {stale && (
          <p style={{ color: "#fbbf24" }}>
            Warning: FYERS positions are stale. Refresh before relying on any
            position analysis.
          </p>
        )}

        <p style={{ color: "#fbbf24" }}>Payoff unavailable: {model.error}</p>
      </section>
    );
  }

  const minStrike = model.strikes[0];
  const maxStrike = model.strikes[model.strikes.length - 1];
  const strikeSpan = maxStrike - minStrike;

  const padding = Math.max(
    strikeSpan * 0.2,
    (currentSpot || maxStrike) * 0.04,
    100,
  );

  const lowPrice = Math.max(
    0,
    Math.min(minStrike, currentSpot ?? minStrike) - padding,
  );

  const highPrice = Math.max(maxStrike, currentSpot ?? maxStrike) + padding;

  const breakpoints = [
    lowPrice,
    ...model.strikes.filter(
      (strike) => strike > lowPrice && strike < highPrice,
    ),
    highPrice,
  ];

  const breakevens = findPayoffBreakevens(model.payoffAt, breakpoints);

  // Include evenly spaced samples and exact strikes so the plotted
  // line follows the piecewise-linear expiry payoff.
  const samplePrices = Array.from(
    { length: 101 },
    (_, i) => lowPrice + ((highPrice - lowPrice) * i) / 100,
  );

  const prices = [
    ...new Set([
      ...samplePrices,
      ...model.strikes.filter(
        (strike) => strike >= lowPrice && strike <= highPrice,
      ),
      ...breakevens,
      ...(currentSpot === null ? [] : [currentSpot]),
    ]),
  ].sort((a, b) => a - b);

  const chartPoints = prices.map((price) => ({
    price,
    pnl: model.payoffAt(price),
  }));

  const pnlValues = chartPoints.map((point) => point.pnl);
  const rawMinPnl = Math.min(...pnlValues, 0);
  const rawMaxPnl = Math.max(...pnlValues, 0);
  const pnlPadding = Math.max((rawMaxPnl - rawMinPnl) * 0.12, 100);

  const minPnl = rawMinPnl - pnlPadding;
  const maxPnl = rawMaxPnl + pnlPadding;

  const xScale = (price) =>
    margin.left + ((price - lowPrice) / (highPrice - lowPrice)) * plotWidth;

  const yScale = (pnl) =>
    margin.top + ((maxPnl - pnl) / (maxPnl - minPnl)) * plotHeight;

  const path = chartPoints
    .map(
      (point, i) =>
        `${i === 0 ? "M" : "L"} ${xScale(point.price).toFixed(2)} ${yScale(point.pnl).toFixed(2)}`,
    )
    .join(" ");

  const zeroY = yScale(0);
  const zeroVisible = minPnl <= 0 && maxPnl >= 0;

  const expiryLabel = model.expiry;

  return (
    <section style={styles.card}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 0 }}>
          Position Payoff — at Expiry
        </h3>

        <span style={{ color: "#9ca3af", fontSize: 12 }}>
          {model.index} · {expiryLabel} · theoretical expiry P&L
        </span>
      </div>

      {stale && (
        <p style={{ color: "#fbbf24" }}>
          Warning: the broker snapshot is stale. The position-based curve may
          not represent your current positions.
        </p>
      )}

      {loading && (
        <p style={{ color: "#fbbf24" }}>
          Refresh in progress. Values may update when FYERS responds.
        </p>
      )}

      <p style={{ color: "#9ca3af", fontSize: 13 }}>
        This curve shows theoretical profit or loss at expiry after option time
        value has fallen to zero. It is not a forecast of live P&L before
        expiry.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10,
          marginTop: 14,
        }}
      >
        <div style={panelStyle}>
          <div style={labelStyle}>Best expiry P&L</div>
          <div
            style={{
              ...numberStyle,
              color: model.maxProfitUnlimited
                ? "#34d399"
                : pnlColor(model.bestExpiryPnl),
            }}
          >
            {model.maxProfitUnlimited
              ? "Unlimited"
              : formatPnl(model.bestExpiryPnl)}
          </div>
        </div>

        <div style={panelStyle}>
          <div style={labelStyle}>Worst expiry P&L</div>
          <div
            style={{
              ...numberStyle,
              color: model.maxLossUnlimited
                ? "#f87171"
                : pnlColor(model.worstExpiryPnl),
            }}
          >
            {model.maxLossUnlimited
              ? "Unlimited loss"
              : formatPnl(model.worstExpiryPnl)}
          </div>
        </div>

        <div style={panelStyle}>
          <div style={labelStyle}>Current {index} spot</div>
          <div style={numberStyle}>
            {currentSpot === null ? "Unavailable" : money(currentSpot)}
          </div>
        </div>

        <div style={panelStyle}>
          <div style={labelStyle}>Expiry P&L at spot</div>
          <div
            style={{
              ...numberStyle,
              color:
                currentSpot === null
                  ? "#9ca3af"
                  : pnlColor(model.payoffAt(currentSpot)),
            }}
          >
            {currentSpot === null
              ? "Unavailable"
              : formatPnl(model.payoffAt(currentSpot))}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 18, ...styles.tableWrap }}>
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          role="img"
          aria-label="Theoretical options expiry payoff chart"
          style={{
            width: "100%",
            minWidth: 320,
            display: "block",
          }}
        >
          <rect
            x={margin.left}
            y={margin.top}
            width={plotWidth}
            height={plotHeight}
            fill="#0b1220"
            stroke="#374151"
          />

          {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
            const y = margin.top + fraction * plotHeight;
            const pnl = maxPnl - fraction * (maxPnl - minPnl);

            return (
              <g key={`y-${fraction}`}>
                <line
                  x1={margin.left}
                  x2={margin.left + plotWidth}
                  y1={y}
                  y2={y}
                  stroke="#374151"
                  strokeDasharray="3 4"
                />
                <text
                  x={margin.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  fill="#9ca3af"
                  fontSize="11"
                >
                  {Math.round(pnl).toLocaleString("en-IN")}
                </text>
              </g>
            );
          })}

          {zeroVisible && (
            <line
              x1={margin.left}
              x2={margin.left + plotWidth}
              y1={zeroY}
              y2={zeroY}
              stroke="#d1d5db"
              strokeDasharray="5 4"
              strokeWidth="1.5"
            />
          )}

          {model.strikes
            .filter((strike) => strike >= lowPrice && strike <= highPrice)
            .map((strike) => (
              <g key={`strike-${strike}`}>
                <line
                  x1={xScale(strike)}
                  x2={xScale(strike)}
                  y1={margin.top}
                  y2={margin.top + plotHeight}
                  stroke="#374151"
                  strokeDasharray="2 5"
                />
                <text
                  x={xScale(strike)}
                  y={margin.top + plotHeight + 18}
                  textAnchor="middle"
                  fill="#9ca3af"
                  fontSize="10"
                >
                  {strike}
                </text>
              </g>
            ))}

          {breakevens
            .filter((root) => root >= lowPrice && root <= highPrice)
            .map((root) => (
              <g key={`be-${root}`}>
                <circle
                  cx={xScale(root)}
                  cy={zeroY}
                  r="4"
                  fill="#fbbf24"
                  stroke="#111827"
                  strokeWidth="1"
                />
              </g>
            ))}

          {currentSpot !== null &&
            currentSpot >= lowPrice &&
            currentSpot <= highPrice && (
              <g>
                <line
                  x1={xScale(currentSpot)}
                  x2={xScale(currentSpot)}
                  y1={margin.top}
                  y2={margin.top + plotHeight}
                  stroke="#60a5fa"
                  strokeWidth="1.5"
                  strokeDasharray="5 4"
                />
                <text
                  x={Math.min(
                    xScale(currentSpot) + 5,
                    margin.left + plotWidth - 48,
                  )}
                  y={margin.top + 12}
                  fill="#60a5fa"
                  fontSize="11"
                >
                  Spot
                </text>
              </g>
            )}

          <path
            d={path}
            fill="none"
            stroke="#34d399"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          <text
            x={margin.left + plotWidth / 2}
            y={chartHeight - 7}
            textAnchor="middle"
            fill="#9ca3af"
            fontSize="12"
          >
            Underlying price at expiry
          </text>

          <text
            x="14"
            y={margin.top + plotHeight / 2}
            transform={`rotate(-90 14 ${margin.top + plotHeight / 2})`}
            textAnchor="middle"
            fill="#9ca3af"
            fontSize="12"
          >
            Expiry P&L (₹)
          </text>
        </svg>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          marginTop: 10,
          fontSize: 12,
          color: "#9ca3af",
        }}
      >
        <span>
          <span style={{ color: "#34d399" }}>━</span> Expiry payoff
        </span>
        <span>
          <span style={{ color: "#60a5fa" }}>┆</span> Current spot
        </span>
        <span>
          <span style={{ color: "#fbbf24" }}>●</span> Breakeven
        </span>
        <span>Breakevens: {breakevens.length}</span>
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ ...labelStyle, marginBottom: 6 }}>Breakeven price(s)</div>

        {breakevens.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {breakevens.map((price) => (
              <span
                key={price.toFixed(2)}
                style={{
                  background: "#374151",
                  borderRadius: 6,
                  padding: "7px 10px",
                  color: "#fbbf24",
                }}
              >
                {money(price)}
              </span>
            ))}
          </div>
        ) : (
          <p style={{ color: "#9ca3af" }}>
            No breakeven was found in the non-negative underlying price range.
          </p>
        )}
      </div>

      <p style={{ color: "#9ca3af", fontSize: 12, marginBottom: 0 }}>
        Calculated from current broker net quantities and average entry prices.
        Brokerage, taxes, slippage, non-option positions, and live option time
        value are excluded. Unlimited outcomes are identified from the
        portfolio's call exposure; finite values are evaluated at zero
        underlying price and the listed strikes.
      </p>
    </section>
  );
}

function LiveTrade() {
  const [selectedIndex, setSelectedIndex] = useState("NIFTY");
  const [data, setData] = useState(null);

  const [brokerPositions, setBrokerPositions] = useState([]);
  const [brokerPositionsLoading, setBrokerPositionsLoading] = useState(false);
  const [brokerPositionsError, setBrokerPositionsError] = useState("");
  const [brokerPositionsStale, setBrokerPositionsStale] = useState(false);
  const [brokerPositionsUpdatedAt, setBrokerPositionsUpdatedAt] =
    useState(null);

  // Strike terminal controls and local multi-selection queue.
  const [strikeCount, setStrikeCount] = useState(10);
  const [defaultLots, setDefaultLots] = useState(1);
  const [multipleMode, setMultipleMode] = useState(false);
  const [confirmationEnabled, setConfirmationEnabled] = useState(true);
  const [selectedActions, setSelectedActions] = useState([]);
  const [strikeActionNotice, setStrikeActionNotice] = useState("");
  const [connected, setConnected] = useState(socket.connected);

  // Keep the latest index available to the connect handler.
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;

  const handleIndexChange = (nextIndex) => {
    if (!["NIFTY", "SENSEX"].includes(nextIndex)) return;

    if (nextIndex === selectedIndex) return;

    // Do not carry queued orders into another index.
    if (selectedActions.length > 0) {
      setStrikeActionNotice("Clear selected actions before switching indexes.");
      return;
    }

    selectedIndexRef.current = nextIndex;
    setSelectedIndex(nextIndex);
    setData(null);

    setStrikeActionNotice(
      `Switching to ${nextIndex}. Waiting for market data...`,
    );

    if (socket.connected) {
      socket.emit("selectIndex", nextIndex);
    }
  };

  useEffect(() => {
    const onConnect = () => {
      setConnected(true);

      socket.emit("selectIndex", selectedIndexRef.current);

      setBrokerPositionsLoading(true);
      setBrokerPositionsError("");
      socket.emit("getBrokerPositions");
    };

    const markConnectionLost = (message) => {
      setConnected(false);
      setBrokerPositionsStale(true);
      setBrokerPositionsLoading(false);
      setBrokerPositionsError(message);
    };

    const onDisconnect = () => {
      markConnectionLost("Backend disconnected. Live positions may be stale.");
    };

    const onConnectError = (err) => {
      markConnectionLost(
        `Socket connection failed: ${err?.message || "Unknown connection error"}.`,
      );
    };

    const onStrategyData = (payload) => {
      setData(payload);
    };

    const onSelectedIndex = (index) => {
      if (!["NIFTY", "SENSEX"].includes(index)) return;

      selectedIndexRef.current = index;
      setSelectedIndex(index);
      setData(null);
    };

    const onBrokerPositions = (payload) => {
      if (!Array.isArray(payload?.positions)) {
        setBrokerPositionsStale(true);
        setBrokerPositionsError(
          "The backend returned an invalid broker positions response.",
        );
        setBrokerPositionsLoading(false);
        return;
      }

      setBrokerPositions(payload.positions);
      setBrokerPositionsError("");
      setBrokerPositionsStale(false);
      setBrokerPositionsUpdatedAt(
        payload.timestamp || new Date().toISOString(),
      );
      setBrokerPositionsLoading(false);
    };

    const onBrokerPositionsError = (payload) => {
      // Preserve the last successful snapshot. A fetch error does not mean flat.
      setBrokerPositionsStale(true);
      setBrokerPositionsError(
        payload?.message || "Unable to fetch live FYERS positions.",
      );
      setBrokerPositionsLoading(false);
    };

    const onBrokerPositionsMonitorError = (payload) => {
      // Background polling failed; retain the last known broker positions.
      setBrokerPositionsStale(true);
      setBrokerPositionsError(
        payload?.message || "Background FYERS position monitoring failed.",
      );
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("strategyData", onStrategyData);
    socket.on("selectedIndex", onSelectedIndex);
    socket.on("brokerPositions", onBrokerPositions);
    socket.on("brokerPositionsError", onBrokerPositionsError);
    socket.on("brokerPositionsMonitorError", onBrokerPositionsMonitorError);

    socket.connect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("strategyData", onStrategyData);
      socket.off("selectedIndex", onSelectedIndex);
      socket.off("brokerPositions", onBrokerPositions);
      socket.off("brokerPositionsError", onBrokerPositionsError);
      socket.off("brokerPositionsMonitorError", onBrokerPositionsMonitorError);

      socket.disconnect();
    };
  }, []);

  const liveBrokerPositions = (brokerPositions || []).filter((position) => {
    const netQty = Number(position?.netQty);

    const isOpenPosition = Number.isFinite(netQty) && netQty !== 0;

    return isOpenPosition && positionBelongsToIndex(position, selectedIndex);
  });

  const brokerPositionsByStrike = liveBrokerPositions.reduce(
    (groups, position) => {
      const parsed = parseOptionSymbol(position.symbol);

      if (!parsed) return groups;

      const { strike, optionType } = parsed;

      if (!groups.has(strike)) {
        groups.set(strike, { PE: [], CE: [] });
      }

      groups.get(strike)[optionType].push(position);

      return groups;
    },
    new Map(),
  );

  const brokerPositionStrikes = [...brokerPositionsByStrike.keys()].sort(
    (a, b) => Number(b) - Number(a),
  );

  const otherBrokerPositions = liveBrokerPositions.filter(
    (position) => !parseOptionSymbol(position.symbol),
  );

  const strikeStep = selectedIndex === "NIFTY" ? 50 : 100;
  const atmStrike =
    Number(data?.strike) > 0
      ? Number(data.strike)
      : Number(data?.spot) > 0
        ? Math.round(Number(data.spot) / strikeStep) * strikeStep
        : null;

  const ladderStrikes =
    atmStrike === null
      ? []
      : Array.from(
          { length: strikeCount * 2 + 1 },
          (_, rowIndex) => atmStrike + (strikeCount - rowIndex) * strikeStep,
        );

  // Include every actual open-position strike,
  // even if it is outside the ATM ladder.
  const allDisplayedStrikes = [
    ...new Set([...ladderStrikes, ...brokerPositionStrikes.map(Number)]),
  ].sort((a, b) => b - a);

  const surroundingStrikeRows = allDisplayedStrikes.map((strike) => {
    const positions = brokerPositionsByStrike.get(String(strike)) || {
      PE: [],
      CE: [],
    };

    return {
      strike,
      PE:
        positions.PE.find((position) => Number(position.netQty) !== 0) || null,
      CE:
        positions.CE.find((position) => Number(position.netQty) !== 0) || null,
    };
  });

  // --------------------------------------------------
  // NEW: Send displayed strikes to backend for LTP feed.
  // --------------------------------------------------

  const brokerPositionStrikeKey = brokerPositionStrikes.join(",");

  useEffect(() => {
    if (!connected || !socket.connected) return;

    socket.emit("setOptionLtpStrikes", {
      strikeCount,
      extraStrikes: brokerPositionStrikeKey
        ? brokerPositionStrikeKey.split(",").map(Number)
        : [],
    });
  }, [connected, selectedIndex, strikeCount, brokerPositionStrikeKey]);

  const confirmOrderAction = (message) => {
    return !confirmationEnabled || window.confirm(message);
  };

  const submitManualOrderRequest = (eventName, payload, successMessage) => {
    if (!socket.connected) {
      setStrikeActionNotice("Backend disconnected. Order request blocked.");
      return;
    }

    setStrikeActionNotice(
      "Submitting order. Waiting for FYERS confirmation...",
    );

    socket.timeout(60000).emit(eventName, payload, (err, response) => {
      if (err) {
        setStrikeActionNotice(
          "No backend acknowledgement received. Order status may be uncertain. " +
            "Check the FYERS order book and positions before retrying.",
        );
        return;
      }

      if (response?.status === "success") {
        setStrikeActionNotice(response.message || successMessage);
        return;
      }

      setStrikeActionNotice(
        response?.message || "Order was not confirmed by the backend.",
      );
    });
  };

  const queueStrikeAction = ({
    strike,
    action,
    optionType = null,
    position = null,
  }) => {
    if (!connected || !socket.connected) {
      setStrikeActionNotice("Backend disconnected. Order request blocked.");
      return;
    }

    const expiryTs = Number(data?.expiryTs);

    // New BUY / SELL orders require the active weekly expiry.
    if (action !== "EXIT" && action !== "EXIT_STRADDLE") {
      if (!Number.isFinite(expiryTs) || expiryTs <= 0) {
        setStrikeActionNotice(
          "Current weekly expiry is unavailable. Refresh market data.",
        );
        return;
      }
    }

    const makeItem = (side, type) => ({
      id: `${selectedIndex}-${strike}-${type}-${side}-${Date.now()}-${Math.random()}`,
      index: selectedIndex,
      expiryTs,
      strike: Number(strike),
      action: side,
      optionType: type,
      lots: defaultLots,
      symbol: null,
      quantity: null,
      exitSide: null,
    });

    const makeExitItem = (positionToExit, type) => {
      const netQty = Number(
        positionToExit?.netQty ??
          positionToExit?.netqty ??
          positionToExit?.net_qty ??
          0,
      );

      if (!positionToExit?.symbol || !Number.isFinite(netQty) || netQty === 0) {
        return null;
      }

      return {
        id: `${selectedIndex}-${strike}-${type}-EXIT-${Date.now()}-${Math.random()}`,
        index: selectedIndex,
        strike: Number(strike),
        action: "EXIT",
        optionType: type,
        symbol: positionToExit.symbol,
        quantity: Math.abs(netQty),
        exitSide: netQty > 0 ? "SELL" : "BUY",
        lots: null,
      };
    };

    // --------------------------------------------------
    // EXIT STRADDLE: close both existing CE and PE legs.
    // --------------------------------------------------
    if (action === "EXIT_STRADDLE") {
      if (brokerPositionsStale || brokerPositionsLoading) {
        setStrikeActionNotice(
          "Refresh FYERS positions successfully before executing SS Exit.",
        );
        return;
      }

      const cePosition = position?.CE;
      const pePosition = position?.PE;

      const ceParsed = parseOptionSymbol(cePosition?.symbol);
      const peParsed = parseOptionSymbol(pePosition?.symbol);

      const ceQty = Number(cePosition?.netQty);
      const peQty = Number(pePosition?.netQty);

      // SS Exit is only for an existing short CE + short PE.
      if (
        !cePosition ||
        !pePosition ||
        !Number.isFinite(ceQty) ||
        !Number.isFinite(peQty) ||
        ceQty >= 0 ||
        peQty >= 0 ||
        !ceParsed ||
        !peParsed ||
        ceParsed.index !== selectedIndex ||
        peParsed.index !== selectedIndex ||
        ceParsed.expiry !== peParsed.expiry ||
        ceParsed.strike !== String(strike) ||
        peParsed.strike !== String(strike) ||
        ceParsed.optionType !== "CE" ||
        peParsed.optionType !== "PE"
      ) {
        setStrikeActionNotice(
          "SS Exit blocked. Both CE and PE must be valid short positions with the same expiry and strike.",
        );
        return;
      }

      const ceExit = makeExitItem(cePosition, "CE");
      const peExit = makeExitItem(pePosition, "PE");

      if (!ceExit || !peExit) {
        setStrikeActionNotice("Unable to prepare both straddle exits.");
        return;
      }

      // Multiple mode: queue both legs. No confirmation yet.
      if (multipleMode) {
        setSelectedActions((current) => [...current, ceExit, peExit]);

        setStrikeActionNotice(`EXIT ${strike} CE + PE added to selections.`);
        return;
      }

      // Immediate mode: confirm once for both legs.
      if (
        !confirmOrderAction(
          `Close SELL straddle at ${strike}?\n\n` +
            `BUY ${ceExit.quantity} units of ${ceExit.symbol}\n` +
            `BUY ${peExit.quantity} units of ${peExit.symbol}`,
        )
      ) {
        return;
      }

      const payload = {
        index: selectedIndex,
        straddleExit: true,
        categories: {
          buys: [],
          exits: [ceExit, peExit],
          sells: [],
        },
      };

      setStrikeActionNotice(`Submitting SS Exit for ${strike} CE + PE...`);

      socket
        .timeout(300000)
        .emit("manualExecuteOrderBatch", payload, (err, response) => {
          if (err) {
            setStrikeActionNotice(
              "No backend acknowledgement received. Check FYERS positions before retrying.",
            );
            return;
          }

          setStrikeActionNotice(
            response?.message ||
              (response?.status === "success"
                ? `SS Exit processed for ${strike} CE + PE.`
                : "SS Exit was not confirmed by the backend."),
          );
        });

      return;
    }

    // --------------------------------------------------
    // EXIT: close one existing position.
    // --------------------------------------------------
    if (action === "EXIT") {
      const exitItem = makeExitItem(position, optionType);

      if (!exitItem) {
        setStrikeActionNotice("Invalid broker position. Exit blocked.");
        return;
      }

      if (multipleMode) {
        setSelectedActions((current) => [...current, exitItem]);
        setStrikeActionNotice(
          `EXIT ${strike} ${optionType} added to selections.`,
        );
        return;
      }

      if (
        !confirmOrderAction(
          `Confirm EXIT ${strike} ${optionType}?\n\n` +
            `${exitItem.exitSide} ${exitItem.quantity} units of ${exitItem.symbol}`,
        )
      ) {
        return;
      }

      submitManualOrderRequest(
        "manualExitOrder",
        {
          index: selectedIndex,
          order: exitItem,
        },
        `EXIT request submitted for ${position.symbol}.`,
      );

      return;
    }

    // --------------------------------------------------
    // SS: place two SELL orders, one CE and one PE.
    // --------------------------------------------------
    if (action === "SELL_STRADDLE") {
      const ceItem = makeItem("SELL", "CE");
      const peItem = makeItem("SELL", "PE");

      if (multipleMode) {
        setSelectedActions((current) => [...current, ceItem, peItem]);

        setStrikeActionNotice(`SELL ${strike} CE + PE added to selections.`);
        return;
      }

      if (
        !confirmOrderAction(
          `Confirm SELL straddle at ${strike}?\n\n` +
            `SELL ${defaultLots} lot(s) CE\n` +
            `SELL ${defaultLots} lot(s) PE`,
        )
      ) {
        return;
      }

      submitManualOrderRequest(
        "manualPlaceMultipleOrders",
        {
          index: selectedIndex,
          expiryTs,
          orders: [ceItem, peItem],
        },
        `Two-leg SELL request submitted for ${strike} CE + PE.`,
      );

      return;
    }

    // --------------------------------------------------
    // Individual BUY or SELL.
    // --------------------------------------------------
    const item = makeItem(action, optionType);

    if (multipleMode) {
      setSelectedActions((current) => [...current, item]);

      setStrikeActionNotice(
        `${action} ${strike} ${optionType} added to selections.`,
      );
      return;
    }

    if (
      !confirmOrderAction(
        `Confirm ${action} ${strike} ${optionType}?\n\n` +
          `${defaultLots} lot(s)`,
      )
    ) {
      return;
    }

    submitManualOrderRequest(
      "manualPlaceOrder",
      {
        index: selectedIndex,
        expiryTs,
        order: item,
      },
      `${action} request submitted for ${strike} ${optionType}.`,
    );
  };

  const updateQueuedLots = (id, value) => {
    setSelectedActions((current) =>
      current.map((item) =>
        item.id === id
          ? { ...item, lots: Math.max(1, Number(value) || 1) }
          : item,
      ),
    );
  };

  const executeSelectedActions = () => {
    if (!selectedActions.length) {
      setStrikeActionNotice("No actions selected.");
      return;
    }

    if (!connected || !socket.connected) {
      setStrikeActionNotice("Backend disconnected. Execution blocked.");
      return;
    }

    const hasExits = selectedActions.some((item) => item.action === "EXIT");

    if (hasExits && (brokerPositionsStale || brokerPositionsLoading)) {
      setStrikeActionNotice(
        "Refresh FYERS positions successfully before executing exits.",
      );
      return;
    }

    const buys = selectedActions.filter((item) => item.action === "BUY");

    const exits = selectedActions.filter((item) => item.action === "EXIT");

    const sells = selectedActions.filter((item) => item.action === "SELL");

    const invalidBuySell = [...buys, ...sells].some((item) => {
      const lots = Number(item.lots);
      const expiryTs = Number(item.expiryTs);

      return (
        !Number.isInteger(lots) ||
        lots < 1 ||
        lots > 100 ||
        !Number.isFinite(expiryTs) ||
        expiryTs <= 0 ||
        !Number.isFinite(Number(item.strike)) ||
        !["CE", "PE"].includes(item.optionType)
      );
    });

    if (invalidBuySell) {
      setStrikeActionNotice(
        "Invalid BUY/SELL selection. Check expiry, strike, option type and lots.",
      );
      return;
    }

    const invalidExits = exits.some((item) => {
      const quantity = Number(item.quantity);

      return (
        !item.symbol ||
        !Number.isInteger(quantity) ||
        quantity <= 0 ||
        !["BUY", "SELL"].includes(item.exitSide)
      );
    });

    if (invalidExits) {
      setStrikeActionNotice(
        "Invalid EXIT selection. Refresh positions and select the exit again.",
      );
      return;
    }

    const indexes = new Set(selectedActions.map((item) => item.index));

    if (indexes.size !== 1 || !indexes.has(selectedIndex)) {
      setStrikeActionNotice(
        "Selections contain different indexes. Clear the list and select again.",
      );
      return;
    }

    // Ask only when Confirmation is enabled.
    if (
      !confirmOrderAction(
        `Execute ${selectedActions.length} action(s) for ${selectedIndex}?\n\n` +
          `BUY: ${buys.length}\n` +
          `EXIT: ${exits.length}\n` +
          `SELL: ${sells.length}\n\n` +
          `Execution order: BUY → EXIT → SELL`,
      )
    ) {
      return;
    }

    const payload = {
      index: selectedIndex,
      categories: {
        buys,
        exits,
        sells,
      },
    };

    setStrikeActionNotice(
      "Submitting selected actions. Waiting for backend acknowledgement...",
    );

    socket
      .timeout(300000)
      .emit("manualExecuteOrderBatch", payload, (err, response) => {
        if (err) {
          setStrikeActionNotice(
            "No backend acknowledgement received. Your selection list is retained. " +
              "Check FYERS positions before attempting execution again.",
          );
          return;
        }

        if (response?.status === "success") {
          setSelectedActions([]);
          setStrikeActionNotice(
            "Backend confirmed successful processing of all selected actions.",
          );
          return;
        }

        setStrikeActionNotice(
          response?.message ||
            "Execution was not fully confirmed. Your selection list is retained. Check FYERS positions before retrying.",
        );
      });
  };

  return (
    <div className="live-trade-page" style={styles.page}>
      <style>{`
  /* Smooth button interaction */
  .live-trade-page button {
    transition:
      filter 0.18s ease,
      transform 0.15s ease,
      box-shadow 0.18s ease;
  }

  /* Hover effect for mouse users */
  @media (hover: hover) and (pointer: fine) {
    .live-trade-page button:not(:disabled):hover {
      filter: brightness(1.22);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
    }
  }

  /* Press / click effect */
  .live-trade-page button:not(:disabled):active {
    filter: brightness(0.92);
    transform: translateY(1px) scale(0.97);
    box-shadow: none;
  }

  /* Keyboard focus effect */
  .live-trade-page button:focus-visible {
    outline: 2px solid #60a5fa;
    outline-offset: 3px;
  }

  /* Disabled buttons */
  .live-trade-page button:disabled {
    cursor: not-allowed;
    box-shadow: none;
  }

  /* Respect reduced-motion preferences */
  @media (prefers-reduced-motion: reduce) {
    .live-trade-page button {
      transition: none;
    }

    .live-trade-page button:not(:disabled):hover,
    .live-trade-page button:not(:disabled):active {
      transform: none;
    }
  }
`}</style>

      {/* Live FYERS positions + ATM strike execution terminal */}
      <section style={{ ...styles.card, marginTop: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>Live FYERS Broker Positions</h3>
            <p style={{ color: "#9ca3af", fontSize: 12, margin: "5px 0 0" }}>
              {selectedIndex} · ATM {atmStrike ?? "waiting for market data"} ·
              {brokerPositionsUpdatedAt
                ? ` Positions updated ${formatDateTime(brokerPositionsUpdatedAt)}`
                : " Waiting for broker positions"}
            </p>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "end",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ ...styles.field, width: 110 }}>
              <label style={styles.label} htmlFor="indexSelector">
                INDEX
              </label>

              <select
                id="indexSelector"
                value={selectedIndex}
                onChange={(event) => handleIndexChange(event.target.value)}
                style={styles.input}
              >
                <option value="NIFTY">NIFTY</option>
                <option value="SENSEX">SENSEX</option>
              </select>
            </div>

            <div style={{ ...styles.field, width: 92 }}>
              <label style={styles.label} htmlFor="strikeCount">
                STRIKES ±
              </label>
              <input
                id="strikeCount"
                type="number"
                min="1"
                max="100"
                value={strikeCount}
                onChange={(event) =>
                  setStrikeCount(
                    Math.min(100, Math.max(1, Number(event.target.value) || 1)),
                  )
                }
                style={styles.input}
              />
            </div>
            <div style={{ ...styles.field, width: 78 }}>
              <label style={styles.label} htmlFor="defaultLots">
                LOTS
              </label>
              <input
                id="defaultLots"
                type="number"
                min="1"
                max="100"
                value={defaultLots}
                onChange={(event) =>
                  setDefaultLots(
                    Math.min(100, Math.max(1, Number(event.target.value) || 1)),
                  )
                }
                style={styles.input}
              />
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                height: 36,
                padding: "0 10px",
                border: "1px solid #4b5563",
                borderRadius: 7,
                color: "#f9fafb",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={multipleMode}
                onChange={(event) => setMultipleMode(event.target.checked)}
              />
              Multiple
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                height: 36,
                padding: "0 10px",
                border: "1px solid #4b5563",
                borderRadius: 7,
                color: "#f9fafb",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={confirmationEnabled}
                onChange={(event) =>
                  setConfirmationEnabled(event.target.checked)
                }
              />
              Confirmation
            </label>

            <button
              type="button"
              disabled={!connected || brokerPositionsLoading}
              onClick={() => {
                setBrokerPositionsError("");
                setBrokerPositionsLoading(true);
                socket.emit("getBrokerPositions");
              }}
              style={{
                ...styles.button,
                height: 36,
                background: "#374151",
                color: "#f9fafb",
              }}
            >
              {brokerPositionsLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {brokerPositionsError && (
          <div
            role="alert"
            style={{
              background: "#450a0a",
              color: "#fecaca",
              padding: 10,
              borderRadius: 8,
              marginTop: 12,
              fontSize: 13,
            }}
          >
            {brokerPositionsStale && brokerPositionsUpdatedAt
              ? `Latest refresh failed. Showing the last successful snapshot. ${brokerPositionsError}`
              : `Unable to load live broker positions: ${brokerPositionsError}`}
          </div>
        )}
        {brokerPositionsLoading && (
          <p style={{ color: "#fbbf24", fontSize: 13 }}>
            Fetching current broker positions from FYERS...
          </p>
        )}
        {strikeActionNotice && (
          <div
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 7,
              border: "1px solid #374151",
              background: "#111827",
              color: "#cbd5e1",
              fontSize: 12,
            }}
          >
            {strikeActionNotice}
          </div>
        )}

        <div style={{ ...styles.tableWrap, marginTop: 14 }}>
          <table style={{ ...styles.table, minWidth: 1600 }}>
            <thead>
              <tr>
                <th style={{ ...styles.th, textAlign: "center" }} colSpan={7}>
                  PE
                </th>

                <th
                  style={{
                    ...styles.th,
                    textAlign: "center",
                    color: "#34d399",
                  }}
                  rowSpan={2}
                >
                  PE LTP
                </th>

                <th style={{ ...styles.th, textAlign: "center" }} rowSpan={2}>
                  Strike
                </th>

                <th
                  style={{
                    ...styles.th,
                    textAlign: "center",
                    color: "#34d399",
                  }}
                  rowSpan={2}
                >
                  CE LTP
                </th>

                {/* SS moved beside CE LTP */}
                <th style={{ ...styles.th, textAlign: "center" }} rowSpan={2}>
                  SS
                </th>

                <th style={{ ...styles.th, textAlign: "center" }} colSpan={7}>
                  CE
                </th>
              </tr>

              <tr>
                {[
                  // PE columns
                  "Symbol",
                  "Side",
                  "Qty",
                  "Entry Price",
                  "Current LTP",
                  "Unrealized P&L",
                  "Action",

                  // CE columns — Action moved to the beginning
                  "Action",
                  "Symbol",
                  "Side",
                  "Qty",
                  "Entry Price",
                  "Current LTP",
                  "Unrealized P&L",
                ].map((heading, index) => (
                  <th key={`${heading}-${index}`} style={styles.th}>
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {surroundingStrikeRows.map((row) => {
                const currentWeekLtp =
                  data?.optionLtpByStrike?.[String(row.strike)];

                const peWeekLtp = currentWeekLtp?.PE ?? null;
                const ceWeekLtp = currentWeekLtp?.CE ?? null;

                const renderStrikeLeg = (
                  position,
                  optionType,
                  strike,
                  actionFirst = false,
                ) => {
                  let detailCells;
                  let actionCell;

                  // No existing broker position
                  if (!position) {
                    detailCells = (
                      <>
                        <td style={styles.td}>—</td>
                        <td style={styles.td}>—</td>
                        <td style={styles.td}>—</td>
                        <td style={styles.td}>—</td>
                        <td style={styles.td}>—</td>
                        <td style={styles.td}>—</td>
                      </>
                    );

                    actionCell = (
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 6,
                          }}
                        >
                          <button
                            type="button"
                            title={`Buy ${optionType} ${strike}`}
                            onClick={() =>
                              queueStrikeAction({
                                strike,
                                action: "BUY",
                                optionType,
                              })
                            }
                            style={{
                              ...styles.button,
                              minWidth: 38,
                              padding: "6px 10px",
                              background: "#16a34a",
                              color: "#fff",
                            }}
                          >
                            B
                          </button>

                          <button
                            type="button"
                            title={`Sell ${optionType} ${strike}`}
                            onClick={() =>
                              queueStrikeAction({
                                strike,
                                action: "SELL",
                                optionType,
                              })
                            }
                            style={{
                              ...styles.button,
                              minWidth: 38,
                              padding: "6px 10px",
                              background: "#dc2626",
                              color: "#fff",
                            }}
                          >
                            S
                          </button>
                        </div>
                      </td>
                    );
                  } else {
                    // Existing broker position
                    const netQty = Number(
                      position.netQty ??
                        position.netqty ??
                        position.net_qty ??
                        0,
                    );

                    const side = netQty > 0 ? "BUY" : netQty < 0 ? "SELL" : "—";

                    const entryCandidates = [
                      position.netAvg,
                      netQty > 0 ? position.buyAvg : position.sellAvg,
                      position.buyAvg,
                      position.sellAvg,
                    ];

                    const entryPrice =
                      entryCandidates
                        .map(Number)
                        .find((value) => Number.isFinite(value) && value > 0) ??
                      null;

                    const currentLtp =
                      position.ltp ??
                      position.lp ??
                      position.lastTradedPrice ??
                      position.last_traded_price ??
                      null;

                    const pnl =
                      position.unrealized_profit ??
                      position.unrealizedProfit ??
                      position.pl ??
                      position.pnl ??
                      null;

                    const fullSymbol = String(position.symbol ?? "").trim();

                    const parsedSymbol = parseOptionSymbol(fullSymbol);

                    const shortSymbol = parsedSymbol
                      ? `${parsedSymbol.strike}${parsedSymbol.optionType}`
                      : fullSymbol || "—";

                    detailCells = (
                      <>
                        <td style={styles.td}>
                          <span title={fullSymbol}>{shortSymbol}</span>
                        </td>

                        <td
                          style={{
                            ...styles.td,
                            color:
                              side === "BUY"
                                ? "#34d399"
                                : side === "SELL"
                                  ? "#f87171"
                                  : "#9ca3af",
                            fontWeight: 700,
                          }}
                        >
                          {side}
                        </td>

                        <td style={styles.td}>{Math.abs(netQty)}</td>

                        <td style={styles.td}>
                          {entryPrice != null ? money(entryPrice) : "—"}
                        </td>

                        <td style={styles.td}>
                          {currentLtp != null ? money(currentLtp) : "—"}
                        </td>

                        <td
                          style={{
                            ...styles.td,
                            color:
                              pnl == null
                                ? "#9ca3af"
                                : Number(pnl) >= 0
                                  ? "#34d399"
                                  : "#f87171",
                          }}
                        >
                          {pnl != null ? money(pnl) : "—"}
                        </td>
                      </>
                    );

                    actionCell = (
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <button
                          type="button"
                          title={`Exit ${optionType} ${strike}`}
                          onClick={() =>
                            queueStrikeAction({
                              strike,
                              action: "EXIT",
                              optionType,
                              position,
                            })
                          }
                          style={{
                            ...styles.button,
                            padding: "6px 10px",
                            background: "#f59e0b",
                            color: "#111827",
                          }}
                        >
                          Exit
                        </button>
                      </td>
                    );
                  }

                  // PE keeps its original order.
                  // CE displays Action before Symbol.
                  return actionFirst ? (
                    <>
                      {actionCell}
                      {detailCells}
                    </>
                  ) : (
                    <>
                      {detailCells}
                      {actionCell}
                    </>
                  );
                };

                const sides = new Set(
                  [row.PE, row.CE]
                    .filter(Boolean)
                    .map((position) =>
                      Number(position.netQty) > 0
                        ? "BUY"
                        : Number(position.netQty) < 0
                          ? "SELL"
                          : "—",
                    ),
                );

                const rowColor =
                  sides.has("BUY") && sides.has("SELL")
                    ? "#8a5a35"
                    : sides.has("SELL")
                      ? "#670606"
                      : sides.has("BUY")
                        ? "#044b20"
                        : "transparent";

                const isVacantStrike = !row.PE && !row.CE;

                return (
                  <tr key={row.strike} style={{ background: rowColor }}>
                    {renderStrikeLeg(row.PE, "PE", row.strike)}

                    {/* NEW: Current week PE LTP */}
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "center",
                        color: peWeekLtp != null ? "#34d399" : "#9ca3af",
                        fontWeight: 700,
                        background: "#14251f",
                      }}
                    >
                      {peWeekLtp != null ? money(peWeekLtp) : "—"}
                    </td>

                    <td
                      style={{
                        ...styles.td,
                        textAlign: "center",
                        fontWeight: 800,
                        color: row.strike === atmStrike ? "#fbbf24" : "#f9fafb",
                        background:
                          row.strike === atmStrike ? "#3b3420" : "transparent",
                      }}
                    >
                      {row.strike === atmStrike && (
                        <>
                          {/* <div
                            style={{
                              color: "#fbbf24",
                              fontSize: 10,
                              marginTop: 2,
                              fontWeight: 700,
                            }}
                          >
                            ATM
                          </div> */}

                          <div
                            style={{
                              color: "#ccfaf9",
                              fontSize: 16,
                              marginTop: 2,
                              fontWeight: 700,
                            }}
                          >
                            {data?.spot != null &&
                            Number.isFinite(Number(data.spot)) &&
                            Number(data.spot) > 0
                              ? money(data.spot)
                              : "—"}
                          </div>
                        </>
                      )}
                      {row.strike}
                    </td>

                    {/* NEW: Current week CE LTP */}
                    <td
                      style={{
                        ...styles.td,
                        textAlign: "center",
                        color: ceWeekLtp != null ? "#34d399" : "#9ca3af",
                        fontWeight: 700,
                        background: "#14251f",
                      }}
                    >
                      {ceWeekLtp != null ? money(ceWeekLtp) : "—"}
                    </td>

                    <td style={{ ...styles.td, textAlign: "center" }}>
                      {isVacantStrike ? (
                        <button
                          type="button"
                          title={`Sell CE + PE straddle at ${row.strike}`}
                          onClick={() =>
                            queueStrikeAction({
                              strike: row.strike,
                              action: "SELL_STRADDLE",
                            })
                          }
                          style={{
                            ...styles.button,
                            minWidth: 42,
                            padding: "6px 10px",
                            background: "#7f1d1d",
                            color: "#fff",
                          }}
                        >
                          SS
                        </button>
                      ) : (
                        (() => {
                          const ceQty = Number(row.CE?.netQty);
                          const peQty = Number(row.PE?.netQty);

                          const ceParsed = parseOptionSymbol(row.CE?.symbol);
                          const peParsed = parseOptionSymbol(row.PE?.symbol);

                          const canExitStraddle =
                            row.CE &&
                            row.PE &&
                            ceQty < 0 &&
                            peQty < 0 &&
                            ceParsed &&
                            peParsed &&
                            ceParsed.index === selectedIndex &&
                            peParsed.index === selectedIndex &&
                            ceParsed.expiry === peParsed.expiry &&
                            ceParsed.strike === String(row.strike) &&
                            peParsed.strike === String(row.strike) &&
                            ceParsed.optionType === "CE" &&
                            peParsed.optionType === "PE";

                          return canExitStraddle ? (
                            <button
                              type="button"
                              title={`Exit SELL CE + PE straddle at ${row.strike}`}
                              onClick={() =>
                                queueStrikeAction({
                                  strike: row.strike,
                                  action: "EXIT_STRADDLE",
                                  position: {
                                    CE: row.CE,
                                    PE: row.PE,
                                  },
                                })
                              }
                              style={{
                                ...styles.button,
                                minWidth: 65,
                                padding: "6px 10px",
                                background: "#f59e0b",
                                color: "#111827",
                              }}
                            >
                              SS Exit
                            </button>
                          ) : (
                            <span style={{ color: "#6b7280" }}>—</span>
                          );
                        })()
                      )}
                    </td>

                    {renderStrikeLeg(row.CE, "CE", row.strike, true)}
                  </tr>
                );
              })}

              {surroundingStrikeRows.length === 0 && (
                <tr>
                  <td style={styles.td} colSpan={18}>
                    Waiting for live spot / ATM strike data...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {selectedActions.length > 0 && (
          <section
            style={{
              marginTop: 16,
              padding: 12,
              background: "#111827",
              border: "1px solid #4b5563",
              borderRadius: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <h4 style={{ margin: 0 }}>
                Selected Actions ({selectedActions.length})
              </h4>
              <button
                type="button"
                onClick={() => setSelectedActions([])}
                style={{
                  ...styles.button,
                  background: "#374151",
                  color: "#f9fafb",
                  padding: "6px 10px",
                }}
              >
                Clear All
              </button>
            </div>
            <div style={styles.tableWrap}>
              <table style={{ ...styles.table, minWidth: 560 }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Index</th>
                    <th style={styles.th}>Strike</th>
                    <th style={styles.th}>Action</th>
                    <th style={styles.th}>Lots / Qty</th>
                    <th style={styles.th}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedActions.map((item) => (
                    <tr key={item.id}>
                      <td style={styles.td}>{item.index}</td>
                      <td style={styles.td}>{item.strike}</td>
                      <td style={styles.td}>
                        {item.action === "EXIT"
                          ? `EXIT ${item.optionType}`
                          : item.action === "SELL_STRADDLE"
                            ? "SELL STRADDLE (CE + PE)"
                            : `${item.action} ${item.optionType}`}
                      </td>
                      <td style={styles.td}>
                        {item.action === "EXIT" ? (
                          `${item.quantity} units`
                        ) : (
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={item.lots}
                            onChange={(event) =>
                              updateQueuedLots(item.id, event.target.value)
                            }
                            style={{ ...styles.input, width: 76, height: 32 }}
                          />
                        )}
                      </td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedActions((current) =>
                              current.filter((entry) => entry.id !== item.id),
                            )
                          }
                          style={{
                            ...styles.button,
                            padding: "5px 9px",
                            background: "#7f1d1d",
                            color: "#fff",
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              <button
                type="button"
                onClick={executeSelectedActions}
                style={{
                  ...styles.button,
                  background: "#2563eb",
                  color: "#fff",
                  padding: "10px 18px",
                }}
              >
                Execute Selected ({selectedActions.length})
              </button>
            </div>
          </section>
        )}

        {otherBrokerPositions.length > 0 && (
          <>
            <h4 style={{ marginTop: 18 }}>Other Broker Positions</h4>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {[
                      "Symbol",
                      "Side",
                      "Net Qty",
                      "Average Price",
                      "Current LTP",
                      "Unrealized P&L",
                      "Product",
                      "Source",
                    ].map((heading) => (
                      <th key={heading} style={styles.th}>
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {otherBrokerPositions.map((position, index) => {
                    const netQty = Number(position.netQty);
                    const side = netQty > 0 ? "BUY" : netQty < 0 ? "SELL" : "—";
                    const entryPrice =
                      position.netAvg ??
                      position.buyAvg ??
                      position.sellAvg ??
                      null;
                    const ltp =
                      position.ltp ?? position.lastTradedPrice ?? null;
                    const pnl =
                      position.unrealized_profit ?? position.pl ?? null;
                    return (
                      <tr
                        key={`${position.symbol}-${position.productType || ""}-${index}`}
                      >
                        <td style={styles.td}>{position.symbol || "—"}</td>
                        <td style={styles.td}>{side}</td>
                        <td style={styles.td}>{Math.abs(netQty)}</td>
                        <td style={styles.td}>
                          {entryPrice != null ? money(entryPrice) : "—"}
                        </td>
                        <td style={styles.td}>
                          {ltp != null ? money(ltp) : "—"}
                        </td>
                        <td style={styles.td}>
                          {pnl != null ? money(pnl) : "—"}
                        </td>
                        <td style={styles.td}>{position.productType ?? "—"}</td>
                        <td style={styles.td}>BROKER OPEN</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <p
          style={{
            color: "#9ca3af",
            fontSize: 12,
            marginBottom: 0,
            marginTop: 12,
          }}
        >
          Open / vacant status is determined from the live FYERS broker position
          snapshot. The strike ladder is centred on the live ATM strike and uses
          the {strikeStep}-point step for {selectedIndex}.
        </p>
      </section>

      {/* Expiry payoff chart — derived from current broker positions */}
      <PayoffChart
        positions={liveBrokerPositions}
        spot={data?.spot}
        stale={brokerPositionsStale}
        loading={brokerPositionsLoading}
        index={selectedIndex}
      />
    </div>
  );
}

export default LiveTrade;
